import {createLogger} from 'redux-logger';
import {
  addListener,
  configureStore,
  createListenerMiddleware,
  ListenerEffectAPI,
  TypedAddListener,
  TypedStartListening,
} from '@reduxjs/toolkit';
import {TypedUseSelectorHook, useDispatch, useSelector} from 'react-redux';
import {storeDataLoadingSlice} from 'services/store-data-loading/slice';
import {homeSlice} from 'services/home/slice';
import {campaignClientFormSlice} from 'services/campaign/client-form-slice';
import {hubspotSlice} from 'services/hubspot/slice';
import {brandSlice} from 'services/brand/slice';
import {categorySlice} from 'services/category/slice';
import {cleanItemSlice} from 'services/clean-item/slice';
import {locationConfigurationPresetSlice} from 'services/location-configuration-preset/slice';
import {testControlEventSlice} from 'services/test-control-events/slice';
import {rfpFeasibilitySlice} from 'services/rfp-feasibility/slice';
import {rfpFeasibilitySavedSlice} from 'services/rfp-feasibility/saved-slice';
import {multiVariateAnalysisSlice} from 'services/multi-variate-analysis/slice';
import {listenerMiddleware} from '../utils/middleware-listener';
import {navigationSlice} from '../services/navigation/slice';
import {scheduledSearchSlice} from '../services/scheduledSearch/slice';
import {itemSlice} from '../services/item/slice';
import {campaignTaskSlice} from '../services/campaign-task/slice';
import {storeSlice} from '../services/store/slice';
import {saleSlice} from '../services/sale/slice';
import {userSlice} from '../services/user/slice';
import {digitalShelfSlice} from '../services/digital-shelf/slice';
import {dashboardSlice} from '../services/dashboard/slice';
import {clientSlice} from '../services/client/slice';
import {clientPortalSlice} from '../services/client-portal/slice';
import {inventoryItemListSlice} from '../services/inventory-item-list/slice';
import {campaignSlice} from '../services/campaign/slice';
import {areaSlice} from '../services/area/slice';
import {saleAggregatedSlice} from '../services/salesAggregated/slice';
import {dataLoadingSlice} from '../services/data-loading/slice';
import {dsHealthCheckListSlice} from '../services/ds-health/slice';
import {multiCampaignSlice} from '../services/multi-campaign/slice';
import {analysisSlice} from '../services/campaign/analysis-slice';
import {analysisRunResultsSlice} from '../services/campaign/analysis-run-results-slice';
import {campaignBenchmarkDataSlice} from '../services/campaign/campaign-benchmark-data-slice';
import {systemMessageSlice} from '../services/system-message/slice';
import {emailTemplateSlice} from '../services/email-template/slice';
import {llmRequestSlice} from '../services/llm-request/slice';
import {userManagementSlice} from '../services/user-management/slice';

const rtkListenerMiddleware = createListenerMiddleware();

const middlewares = [listenerMiddleware, rtkListenerMiddleware.middleware] as any[];

if (import.meta.env.DEV) {
  const logger = createLogger({
    collapsed: true,
  });
  middlewares.push(logger);
}

export const store = configureStore({
  reducer: {
    [navigationSlice.name]: navigationSlice.reducer,
    [itemSlice.name]: itemSlice.reducer,
    [campaignTaskSlice.name]: campaignTaskSlice.reducer,
    [storeSlice.name]: storeSlice.reducer,
    [scheduledSearchSlice.name]: scheduledSearchSlice.reducer,
    [saleSlice.name]: saleSlice.reducer,
    [userSlice.name]: userSlice.reducer,
    [digitalShelfSlice.name]: digitalShelfSlice.reducer,
    [dsHealthCheckListSlice.name]: dsHealthCheckListSlice.reducer,
    [dashboardSlice.name]: dashboardSlice.reducer,
    [clientSlice.name]: clientSlice.reducer,
    [clientPortalSlice.name]: clientPortalSlice.reducer,
    [inventoryItemListSlice.name]: inventoryItemListSlice.reducer,
    [campaignSlice.name]: campaignSlice.reducer,
    [areaSlice.name]: areaSlice.reducer,
    [saleAggregatedSlice.name]: saleAggregatedSlice.reducer,
    [dataLoadingSlice.name]: dataLoadingSlice.reducer,
    [storeDataLoadingSlice.name]: storeDataLoadingSlice.reducer,
    [homeSlice.name]: homeSlice.reducer,
    [multiCampaignSlice.name]: multiCampaignSlice.reducer,
    [campaignClientFormSlice.name]: campaignClientFormSlice.reducer,
    [analysisSlice.name]: analysisSlice.reducer,
    [analysisRunResultsSlice.name]: analysisRunResultsSlice.reducer,
    [campaignBenchmarkDataSlice.name]: campaignBenchmarkDataSlice.reducer,
    [hubspotSlice.name]: hubspotSlice.reducer,
    [brandSlice.name]: brandSlice.reducer,
    [categorySlice.name]: categorySlice.reducer,
    [systemMessageSlice.name]: systemMessageSlice.reducer,
    [emailTemplateSlice.name]: emailTemplateSlice.reducer,
    [cleanItemSlice.name]: cleanItemSlice.reducer,
    [locationConfigurationPresetSlice.name]: locationConfigurationPresetSlice.reducer,
    [testControlEventSlice.name]: testControlEventSlice.reducer,
    [llmRequestSlice.name]: llmRequestSlice.reducer,
    [userManagementSlice.name]: userManagementSlice.reducer,
    [rfpFeasibilitySlice.name]: rfpFeasibilitySlice.reducer,
    [rfpFeasibilitySavedSlice.name]: rfpFeasibilitySavedSlice.reducer,
    [multiVariateAnalysisSlice.name]: multiVariateAnalysisSlice.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActionPaths: ['payload.effect', 'payload.actionCreator'],
      },
    }).concat(middlewares),
  // DevTools are automatically enabled by configureStore in development mode
  devTools: import.meta.env.DEV,
});
export type RootState = ReturnType<typeof store.getState>;
// @see https://redux-toolkit.js.org/usage/usage-with-typescript#getting-the-dispatch-type
export type AppDispatch = typeof store.dispatch;

export type AppListenerEffectAPI = ListenerEffectAPI<RootState, AppDispatch>;

// @see https://redux-toolkit.js.org/api/createListenerMiddleware#typescript-usage
export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export type AppAddListener = TypedAddListener<RootState, AppDispatch>;

// export const startAppListening = listenerMiddlewareInstance.startListening as AppStartListening;
export const addAppListener = addListener as AppAddListener;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
